import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
const { useTranslation } = Hooks;
import store from "store";
import Storage from "utils/storage";

import XtScrollArrow from "@/components/Xt/scroll/arrow";

import CPMT_tradingview_nav from "./tradingview/nav";
import CPMT_tradingview_content from "./tradingview/content";

import styles from "./index.module.scss";

export interface OptionTradingViewProps {
  interval?: string; //tradingView周期
  chartType?: number; //设置主数据列的样式, 0=Bar,1=Candle,2=Line,3=Area
}
export function getOptionTradingView(option: WithUndefined<OptionTradingViewProps>) {
  return {
    interval: "15",
    chartType: 1,
    ...(option || Storage.get("tvOption") || {}),
  };
}
export interface ActionTradingViewProps {
  actionId?: string; //操作ID
}

enum ChartTypeEnum {
  tradingview = "tradingview", //TradingView 视图
}

interface KlineProps {
  viewMode?: "standard" | "pro";
  onExpandChange?: (expanded: boolean) => void;
  alwaysExpanded?: boolean; // 是否始终展开（用于详情页）
  initialExpanded?: boolean; // 初始展开状态（由父组件控制）
}

const Main: React.FC<KlineProps> = ({ viewMode, onExpandChange, alwaysExpanded = false, initialExpanded }) => {
  const t = useTranslation();
  const { name, formatCfdName } = store.market;
  // 如果父组件传入了 initialExpanded，完全由父组件控制，否则自己管理状态
  const isControlled = initialExpanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(() => {
    // 初始化时根据 alwaysExpanded 和 viewMode 设置默认值
    if (alwaysExpanded) return true;
    return viewMode !== "pro";
  });
  
  // 使用受控或非受控状态
  const isExpanded = isControlled ? initialExpanded : internalExpanded;
  const setIsExpanded = isControlled 
    ? (value: boolean) => {
        // 受控模式下，通知父组件更新状态
        onExpandChange?.(value);
      }
    : setInternalExpanded;

  const prevViewModeRef = useRef(viewMode);

  // 只在 viewMode 变化时重置展开状态（从标准切换到专业，或从专业切换到标准）
  // 但不在用户手动切换展开/收起后重置，也不在受控模式下重置
  useEffect(() => {
    // 受控模式下，不自己管理状态
    if (isControlled) return;
    
    // 如果 viewMode 没有变化，不重置
    if (prevViewModeRef.current === viewMode) return;
    
    // 如果 alwaysExpanded 为 true，始终展开
    if (alwaysExpanded) {
      setInternalExpanded(true);
      prevViewModeRef.current = viewMode;
      return;
    }
    
    // viewMode 变化时，设置默认状态
    if (viewMode === "pro") {
      setInternalExpanded(false);
    } else {
      setInternalExpanded(true);
    }
    
    prevViewModeRef.current = viewMode;
  }, [viewMode, alwaysExpanded, isControlled]);

  // 通知父组件展开状态变化（只在非受控模式下）
  useEffect(() => {
    if (!isControlled) {
      onExpandChange?.(isExpanded);
    }
  }, [isExpanded, onExpandChange, isControlled]);

  // 动态获取交易对名称
  const chartTitle = useMemo(() => {
    return formatCfdName(name) + t("cfd.contractChart");
  }, [name, formatCfdName, t]);

  const [chartType, setChartType] = useState<ChartTypeEnum>(ChartTypeEnum.tradingview);

  const [option_tradingview, setOption_tradingview] = useState<OptionTradingViewProps>();
  const [action_tradingview, setAction_tradingview] = useState<ActionTradingViewProps>();

  const handleToggleExpand = useCallback(() => {
    const newExpanded = !isExpanded;
    // 如果是受控模式，直接通知父组件更新状态
    if (isControlled) {
      onExpandChange?.(newExpanded);
    } else {
      // 非受控模式，更新内部状态
      setInternalExpanded(newExpanded);
    }
  }, [isExpanded, isControlled, onExpandChange]);

  return (
    <div
      className={cx(styles.main, {
        [styles.standard]: viewMode === "standard",
        [styles.pro]: viewMode === "pro",
        [styles.collapsed]: viewMode === "pro" && !isExpanded && !alwaysExpanded,
      })}
    >
      {/* 专业模式下的展开/收起按钮 */}
      {viewMode === "pro" && !alwaysExpanded && (
        <div className={styles.chartHeader}>
          <div className={styles.chartTitleRow}>
            <span className={styles.chartTitle}>{chartTitle}</span>
            <button className={styles.expandBtn} onClick={handleToggleExpand}>
              <span className={styles.expandText}>{isExpanded ? t("cfd.collapse") : t("cfd.expand")}</span>
              {isExpanded ? (
                // 展开状态：上三角
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="8"
                  height="4"
                  viewBox="0 0 8 4"
                  fill="none"
                  className={styles.expandIcon}
                >
                  <path d="M8 4H0L4 0L8 4Z" fill="#999999" />
                </svg>
              ) : (
                // 收起状态：下三角
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="8"
                  height="4"
                  viewBox="0 0 8 4"
                  fill="none"
                  className={styles.expandIcon}
                >
                  <path d="M8 0H0L4 4L8 0Z" fill="#999999" />
                </svg>
              )}
            </button>
          </div>
        </div>
      )}

      {/* K线图内容 */}
      {(alwaysExpanded || !viewMode || viewMode === "standard" || isExpanded) && (
        <div className={styles.body}>
          <div className={styles.nav}>
            <XtScrollArrow className={styles.navLeft} resetEffect={chartType}>
              {chartType === ChartTypeEnum.tradingview && (
                <CPMT_tradingview_nav
                  option={option_tradingview}
                  setOption={setOption_tradingview}
                  setAction={setAction_tradingview}
                  isStandard={viewMode === "standard"}
                />
              )}
            </XtScrollArrow>
          </div>
          <div className={styles.content}>
            {chartType === ChartTypeEnum.tradingview && (
              <CPMT_tradingview_content
                key={name}
                option={option_tradingview}
                action={action_tradingview}
                isStandard={viewMode === "standard"}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(Main);
